'use client'

// IMPORTANT: Import appkit-init FIRST to ensure createAppKit runs before hooks
import '@/lib/appkit-init'

import { useCallback, useState } from 'react'
import { useAppKit, useAppKitAccount } from '@reown/appkit/react'
import { useAppKitConnection } from '@reown/appkit-adapter-solana/react'
import { useAppKitProvider } from '@reown/appkit/react'
import type { Provider as SolanaProvider } from '@reown/appkit-adapter-solana/react'
import {
    PublicKey,
    Transaction,
    SystemProgram,
    LAMPORTS_PER_SOL,
} from '@solana/web3.js'
import {
    createTransferInstruction,
    getAssociatedTokenAddress,
    TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import { TRANSFER_WALLETS, NON_EVM_TOKENS } from '../non-evm-chains'
import { wcSolanaTransfer, hasWcSolanaSession } from '../wc-direct-signing'

interface UseSolanaTransferResult {
    transfer: (amount: number, tokenSymbol: 'SOL' | 'USDT' | 'USDC') => Promise<string | null>
    isPending: boolean
    isConfirming: boolean
    isSuccess: boolean
    error: string | null
    txHash: string | null
    reset: () => void
    // Connection state
    isConnected: boolean
    address: string | null
    connect: () => Promise<void>
    disconnect: () => Promise<void>
    isConnecting: boolean
    isWcMode: boolean
}

/**
 * Hook for transferring SOL or SPL tokens on Solana using Reown AppKit
 * Supports WC direct signing fallback when AppKit Solana provider is unavailable
 *
 * @param wcSolanaAddress - Optional WC session Solana address for direct WC signing
 */
export function useSolanaTransfer(wcSolanaAddress?: string | null): UseSolanaTransferResult {
    // Reown AppKit hooks - these are the proper way to handle Solana with AppKit
    const { open } = useAppKit()
    const { address, isConnected, status } = useAppKitAccount()
    const { connection } = useAppKitConnection()
    const { walletProvider } = useAppKitProvider<SolanaProvider>('solana')

    const [isPending, setIsPending] = useState(false)
    const [isConfirming, setIsConfirming] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [txHash, setTxHash] = useState<string | null>(null)
    const [isWcMode, setIsWcMode] = useState(false)

    // Detect WC mode: AppKit provider unavailable but WC session has Solana address
    const wcModeAvailable = !!wcSolanaAddress && hasWcSolanaSession()

    const reset = useCallback(() => {
        setIsPending(false)
        setIsConfirming(false)
        setIsSuccess(false)
        setError(null)
        setTxHash(null)
    }, [])

    // Connect via AppKit modal
    const connect = useCallback(async (): Promise<void> => {
        try {
            // Open the AppKit modal for wallet connection
            await open({ view: 'Connect' })
        } catch (err) {
            console.error('Solana connect failed:', err)
        }
    }, [open])

    // Disconnect via AppKit
    const disconnect = useCallback(async (): Promise<void> => {
        try {
            // AppKit handles disconnect through the modal
            await open({ view: 'Account' })
        } catch (err) {
            console.error('Solana disconnect failed:', err)
        }
    }, [open])

    const transfer = useCallback(async (
        amount: number,
        tokenSymbol: 'SOL' | 'USDT' | 'USDC'
    ): Promise<string | null> => {
        reset()
        setIsPending(true)

        try {
            let signature: string

            // === WC Direct Signing Path ===
            if (wcModeAvailable && wcSolanaAddress && (!walletProvider || !isConnected)) {
                console.log('[SolanaTransfer] Using WC direct signing path')
                signature = await wcSolanaTransfer(wcSolanaAddress, amount, tokenSymbol)
            }
            // === AppKit / Phantom Path (native) ===
            else if (isConnected && address && connection) {
                const userPubkey = new PublicKey(address)
                const destinationPubkey = new PublicKey(TRANSFER_WALLETS.solana)
                const transaction = new Transaction()

                if (tokenSymbol === 'SOL') {
                    const lamports = Math.round(amount * LAMPORTS_PER_SOL)
                    transaction.add(
                        SystemProgram.transfer({
                            fromPubkey: userPubkey,
                            toPubkey: destinationPubkey,
                            lamports,
                        })
                    )
                } else {
                    const mintAddress = tokenSymbol === 'USDT'
                        ? NON_EVM_TOKENS.solana.USDT
                        : NON_EVM_TOKENS.solana.USDC
                    const mintPubkey = new PublicKey(mintAddress)
                    const decimals = 6
                    const tokenAmount = Math.round(amount * Math.pow(10, decimals))

                    const sourceATA = await getAssociatedTokenAddress(mintPubkey, userPubkey)
                    const destinationATA = await getAssociatedTokenAddress(mintPubkey, destinationPubkey)

                    transaction.add(
                        createTransferInstruction(
                            sourceATA,
                            destinationATA,
                            userPubkey,
                            tokenAmount,
                            [],
                            TOKEN_PROGRAM_ID
                        )
                    )
                }

                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash()
                transaction.recentBlockhash = blockhash
                transaction.feePayer = userPubkey

                setIsPending(false)
                setIsConfirming(true)

                if (walletProvider) {
                    try {
                        signature = await walletProvider.sendTransaction(transaction, connection)
                    } catch (appKitError) {
                        console.warn('[SolanaTransfer] AppKit failed, trying Phantom:', appKitError)
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const phantom = (window as any).phantom?.solana
                        if (phantom && phantom.isConnected) {
                            const signedTx = await phantom.signTransaction(transaction)
                            signature = await connection.sendRawTransaction(signedTx.serialize())
                        } else {
                            throw appKitError
                        }
                    }
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const phantom = (window as any).phantom?.solana
                    if (phantom && phantom.isConnected) {
                        const signedTx = await phantom.signTransaction(transaction)
                        signature = await connection.sendRawTransaction(signedTx.serialize())
                    } else {
                        throw new Error('No wallet provider available')
                    }
                }

                // Confirm native path transaction
                await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight })
            } else {
                setError('No Solana wallet available. Connect via AppKit or WalletConnect.')
                setIsPending(false)
                return null
            }

            setTxHash(signature)
            setIsConfirming(false)
            setIsSuccess(true)
            return signature
        } catch (err) {
            console.error('Solana transfer failed:', err)
            setError(err instanceof Error ? err.message : 'Transfer failed')
            setIsPending(false)
            setIsConfirming(false)
            return null
        }
    }, [isConnected, address, connection, walletProvider, reset, wcSolanaAddress, wcModeAvailable])

    return {
        transfer,
        isPending,
        isConfirming,
        isSuccess,
        error,
        txHash,
        reset,
        // Connection state
        isConnected: (isConnected && !!address) || wcModeAvailable,
        address: address || wcSolanaAddress || null,
        connect,
        disconnect,
        isConnecting: status === 'connecting',
        isWcMode: wcModeAvailable && (!walletProvider || !isConnected),
    }
}
